import { Suspense, useCallback } from "react";

import { Authenticator } from "@aws-amplify/ui-react";
import { RouterProvider } from "@stacklet/ui";
import { ErrorBoundary } from "react-error-boundary";
import { RelayEnvironmentProvider } from "react-relay/hooks";
import { Outlet, resolvePath, useHref, useNavigate } from "react-router";

import { Login } from "app/components/authentication/Login";
import { CircularFallback, ErrorFallback } from "app/components/fallbacks";
import { UnauthorizedBoundary } from "app/components/PermissionGate/UnauthorizedBoundary";
import { ViewerGate } from "app/components/PermissionGate/ViewerGate";
import { AnalyticsProvider } from "app/contexts";
import { isHttpUrl } from "app/utils/url";

import { handleComponentError } from "./initSentry";
import { RelayEnvironment } from "./RelayEnvironment";

// react-router's useHref treats external URLs as relative paths, breaking
// shouldClientNavigate's same-origin check and routing external links through
// navigate(). This custom hook calls useHref only for internal paths.
// It is invoked by react-aria Link components during their render, satisfying hook rules.
function useRouterSafeHref(href: string): string {
  const resolved = useHref(href);
  return isHttpUrl(href) ? href : resolved;
}

// Wrapper to integrate react-aria-components RouterProvider with react-router.
// RAC's RouterProvider calls navigate() with the raw href prop (not the
// useHref-resolved value), so relative paths would resolve from the root.
// Wrapping navigate to resolve against window.location.pathname at click time fixes this.
// RAC's RouterProvider takes a hook as its `useHref` prop by design, which the
// React Compiler forbids (hooks referenced as values); this file is exempted
// from the compiler rule in eslint.compiler.config.mts.
function ReactAriaRouterProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  const handleNavigate = useCallback(
    (to: string) => {
      if (isHttpUrl(to)) {
        // Fallback: external URL that bypassed shouldClientNavigate (shouldn't normally occur).
        window.location.href = to;
        return;
      }
      navigate(
        to.startsWith("/") ? to : resolvePath(to, window.location.pathname),
      );
    },
    [navigate],
  );

  return (
    <RouterProvider navigate={handleNavigate} useHref={useRouterSafeHref}>
      {children}
    </RouterProvider>
  );
}

/**
 * Root route element: bridges react-aria links into react-router navigation
 * and provides the application-level error boundary.
 */
export function RootLayout() {
  return (
    <ReactAriaRouterProvider>
      <ErrorBoundary
        FallbackComponent={({ resetErrorBoundary }) => (
          <ErrorFallback
            message="Unable to load the application. You may not have the necessary permissions or there may be a system error. If the problem persists, please contact Stacklet support."
            resetErrorBoundary={resetErrorBoundary}
            tryAgain
          />
        )}
        onError={handleComponentError}
      >
        <Outlet />
      </ErrorBoundary>
    </ReactAriaRouterProvider>
  );
}

/**
 * Layout route for everything behind authentication. Password-reset email
 * links must render without authentication, so the reset-password route sits
 * outside this layout, directly under RootLayout.
 */
export function AuthedLayout() {
  return (
    <Authenticator.Provider>
      <Login>
        <RelayEnvironmentProvider environment={RelayEnvironment}>
          <Suspense fallback={<CircularFallback />}>
            <AnalyticsProvider>
              {/* Gate on VIEW before the shell loads; the boundary is the
                  backstop if whoami itself is rejected. */}
              <UnauthorizedBoundary>
                <ViewerGate>
                  <Outlet />
                </ViewerGate>
              </UnauthorizedBoundary>
            </AnalyticsProvider>
          </Suspense>
        </RelayEnvironmentProvider>
      </Login>
    </Authenticator.Provider>
  );
}
